import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';

const initialRoom = {
  room_Category: "",
  roomNo: "",
  room_Charge: "",
  roomSTS: true,
};

const RoomForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialRoom);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);
  

  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const validateField = (name, value) => {
    switch (name) {
      case 'room_Category':
        if (!value.trim()) return 'Required';
        if (value.length < 2) return 'Min 2 characters';
        return '';
      case 'roomNo':
        if (!value) return 'Required';
        return '';
      case 'room_Charge':
        if (value && value < 0) return 'Cannot be negative';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const errors = {};
    errors.room_Category = validateField('room_Category', form.room_Category);
    errors.roomNo = validateField('roomNo', form.roomNo);
    errors.room_Charge = validateField('room_Charge', form.room_Charge);

    const hasErrors = Object.values(errors).some(err => err !== '');
    if (hasErrors) {
      setFieldErrors(errors);
      setTouched({ room_Category: true, roomNo: true, room_Charge: true });
      setError('Please fix form errors');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...form,
        created_By: sessionStorage.getItem("loggedInUser") || "System",
        created_On: new Date().toISOString(),
        edited_By: null,
        edited_On: null,
      };

     const res = await customFetch('/Room', {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.includes("{") ? JSON.parse(text).message : text || "Failed to save");
      }

      setSuccess("Room added successfully!");
      setForm(initialRoom);
      setTouched({});
      setFieldErrors({});
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    navigate('/master/room-list');
  };

  const handleReset = () => {
    setForm(initialRoom);
    setTouched({});
    setFieldErrors({});
    setError('');
    setSuccess('');
  };

  const getFieldClass = (fieldName) => {
    if (!touched[fieldName]) return 'form-control-custom';
    if (fieldErrors[fieldName]) return 'form-control-custom error';
    if (form[fieldName]) return 'form-control-custom success';
    return 'form-control-custom';
  };

  return (
    <div className="room-form-container">
      <div className="room-card">
        {/* Header */}
        <div className="room-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-door-open-fill"></i>
            </div>
            <div className="header-text">
              <h5>Add New Room</h5>
              <p>Register room details and configurations</p>
            </div>
          </div>
          <button className="btn-view-list" onClick={handleButtonClick} title="View Rooms List">
            <i className="bi bi-list-ul"></i> Rooms List
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError('')}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess('')}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="room-form">
          <div className="form-section">
            <div className="section-title">
              <i className="bi bi-info-circle"></i>
              <span>Room Information</span>
            </div>
            <div className="section-content">
              {/* Single Grid for responsiveness */}
              <div className="form-grid">
                
                <div className="form-group">
                  <label htmlFor="room_Category">
                    <i className="bi bi-tag"></i> Category <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <select
                      id="room_Category" name="room_Category"
                      value={form.room_Category} onChange={handleChange} onBlur={handleBlur}
                      className={getFieldClass('room_Category')}
                      tabIndex={1} required disabled={loading}
                    >
                      <option value="" disabled>Select Category</option>
                      <option value="AC">PRIVATE AC ROOM</option>
                      <option value="NON-AC">PRIVATE NON-AC ROOM</option>
                      <option value="GENERAL">GENERAL WARD</option>
                      <option value="ICU">ICU WARD</option>
                      <option value="NICU">NICU WARD</option>
                      <option value="EMERGENCY">EMERGENCY WARD</option>
                       <option value="PRIVATE-ICU-ROOM">PRIVATE ICU ROOM</option>
                     
                    </select>
                  </div>
                  {touched.room_Category && fieldErrors.room_Category && <small className="error-text">{fieldErrors.room_Category}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="roomNo">
                    <i className="bi bi-door-closed"></i> Room Number <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="roomNo" type="text" name="roomNo"
                      value={form.roomNo} onChange={handleChange} onBlur={handleBlur}
                      placeholder="CAT-FLR-BED" className={getFieldClass('roomNo')}
                      tabIndex={2} required disabled={loading}
                    />
                  </div>
                  {touched.roomNo && fieldErrors.roomNo && <small className="error-text">{fieldErrors.roomNo}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="room_Charge">
                    <i className="bi bi-cash-coin"></i> Charge (₹)
                  </label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      id="room_Charge" type="number" name="room_Charge"
                      value={form.room_Charge} onChange={handleChange} onBlur={handleBlur}
                      placeholder="0.00" className={getFieldClass('room_Charge')}
                      style={{ paddingLeft: '24px' }} tabIndex={3} step="0.01" min="0" disabled={loading}
                    />
                  </div>
                  {touched.room_Charge && fieldErrors.room_Charge && <small className="error-text">{fieldErrors.room_Charge}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="roomSTS">
                    <i className="bi bi-toggle-on"></i> Status
                  </label>
                  <select
                    id="roomSTS" name="roomSTS"
                    value={form.roomSTS}
                    onChange={(e) => setForm((prev) => ({ ...prev, roomSTS: e.target.value === "true" }))}
                    className="form-control-custom" tabIndex={4} disabled={loading}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset} disabled={loading}>
              Reset
            </button>
            <button type="submit" className="btn-primary" disabled={loading} tabIndex={5}>
              {loading ? 'Saving...' : 'Save Room'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .room-form-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .room-card {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        /* Header */
        .room-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
          flex-wrap: wrap;
          gap: 10px;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon {
          width: 28px; height: 28px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255,255,255,0.85); font-size: 11px; }

        .btn-view-list {
          padding: 4px 10px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.15); color: white; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 5px;
        }
        .btn-view-list:hover { background: white; color: var(--theme-color, #0088cc); }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 12px; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Form */
        .room-form { padding: 12px; }
        .form-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 12px; }
        
        .section-title {
          background: var(--theme-color, #f7fafc);
          color: white; /* Switch to #2d3748 if theme is light */
          padding: 6px 10px; display: flex; align-items: center; gap: 6px;
          border-bottom: 1px solid #e2e8f0; font-size: 12px; font-weight: 600;
        }
        .section-content { padding: 10px; }

        /* Responsive Grid */
        .form-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label { font-size: 11px; font-weight: 600; color: #4a5568; display: flex; align-items: center; gap: 4px; }
        .form-group label i { color: var(--theme-color, #0088cc); }
        .required { color: #e53e3e; }
        
        .input-wrapper { position: relative; }
        .input-prefix {
          position: absolute; left: 8px; top: 50%; transform: translateY(-50%);
          color: var(--theme-color, #0088cc); font-size: 12px; font-weight: 600;
        }

        .form-control-custom {
          width: 100%; padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 12px; color: #2d3748; outline: none; transition: 0.2s; background: white;
        }
        .form-control-custom:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }
        .form-control-custom.error { border-color: #e53e3e; background: #fff5f5; }
        
        select.form-control-custom {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234a5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat; background-position: right 8px center; background-size: 12px;
          padding-right: 24px;
        }

        .error-text { color: #e53e3e; font-size: 10px; margin-top: 2px; }

        /* Actions */
        .form-actions { display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #e2e8f0; padding-top: 10px; margin-top: 10px; }
        .btn-primary, .btn-secondary {
          padding: 6px 14px; border-radius: 4px; font-size: 12px; font-weight: 600; border: none; cursor: pointer; transition: 0.2s;
        }
        .btn-primary { background: var(--theme-color, #3182ce); color: white; }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-secondary { background: #edf2f7; color: #4a5568; border: 1px solid #cbd5e0; }
        .btn-secondary:hover:not(:disabled) { background: #e2e8f0; }

        /* Responsive Breakpoints */
        @media (max-width: 992px) {
          .form-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .form-grid { grid-template-columns: 1fr; }
          .form-actions { flex-direction: column; }
          .btn-primary, .btn-secondary { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default RoomForm;